<script setup>
import {
    onKeyStroke,
    useDocumentVisibility,
    useElementSize,
    useEventListener,
    useIntersectionObserver,
    usePreferredReducedMotion,
    useRafFn,
    useScrollLock,
} from "@vueuse/core";
import {
    computed,
    onUnmounted,
    provide,
    ref,
    useTemplateRef,
    watch,
} from "vue";

import SceneAskQualta from "./ProcessAnimation/SceneAskQualta.vue";
import SceneGuideName from "./ProcessAnimation/SceneGuideName.vue";
import SceneInterview from "./ProcessAnimation/SceneInterview.vue";
import ScenePublish from "./ProcessAnimation/ScenePublish.vue";
import SceneSocialPost from "./ProcessAnimation/SceneSocialPost.vue";
import SceneSummary from "./ProcessAnimation/SceneSummary.vue";
import SceneTemplate from "./ProcessAnimation/SceneTemplate.vue";
import SceneTitle from "./ProcessAnimation/SceneTitle.vue";
import { createAmbientMusic } from "./ProcessAnimation/ambientMusic";

/** Scenes are laid out on a fixed 16:9 stage, then scaled to fit. */
const STAGE_WIDTH = 640;
const STAGE_HEIGHT = 360;

/** Durations are in ms; one full loop must stay under a minute. */
const scenes = [
    { component: SceneGuideName, duration: 4800 },
    { component: SceneTemplate, duration: 8600 },
    { component: ScenePublish, duration: 6800 },
    { component: SceneSocialPost, duration: 3800 },
    { component: SceneInterview, duration: 11000 },
    { component: SceneSummary, duration: 6500 },
    { component: SceneAskQualta, duration: 8500 },
    { component: SceneTitle, duration: 6000 },
];

const container = useTemplateRef("container");
const { width, height } = useElementSize(container);

/**
 * Fits the stage inside the container and centers it, letterboxing when
 * the container isn't 16:9 (e.g. a fullscreen display).
 */
const stageScale = computed(
    () => Math.min(width.value / STAGE_WIDTH, height.value / STAGE_HEIGHT) || 1,
);
const stageOffset = computed(() => ({
    x: (width.value - STAGE_WIDTH * stageScale.value) / 2,
    y: (height.value - STAGE_HEIGHT * stageScale.value) / 2,
}));

provide("stageScale", stageScale);

const sceneIndex = ref(0);
const sceneTime = ref(0);
const isPausedByUser = ref(false);
const isOnScreen = ref(false);

useIntersectionObserver(container, ([entry]) => {
    isOnScreen.value = entry?.isIntersecting ?? false;
});

const documentVisibility = useDocumentVisibility();
const reducedMotion = usePreferredReducedMotion();
const prefersReducedMotion = computed(() => reducedMotion.value === "reduce");

const fullscreenElement = ref(null);
/** Stands in for fullscreen where the API is missing (iPhone Safari). */
const isPseudoFullscreen = ref(false);
const isFullscreen = computed(
    () =>
        isPseudoFullscreen.value ||
        (fullscreenElement.value !== null &&
            fullscreenElement.value === container.value),
);

const isPlaying = computed(
    () =>
        !prefersReducedMotion.value &&
        !isPausedByUser.value &&
        isOnScreen.value &&
        documentVisibility.value === "visible" &&
        // Another copy of the animation in fullscreen covers this one.
        (fullscreenElement.value === null || isFullscreen.value),
);

const { pause, resume } = useRafFn(
    ({ delta }) => {
        sceneTime.value += Math.min(delta, 100);

        if (sceneTime.value >= scenes[sceneIndex.value].duration) {
            sceneTime.value = 0;
            sceneIndex.value = (sceneIndex.value + 1) % scenes.length;
        }
    },
    { immediate: false },
);

watch(isPlaying, (playing) => (playing ? resume() : pause()), {
    immediate: true,
});

/** Music is opt-in: browsers block audio until the visitor interacts. */
const isMusicOn = ref(false);
let music = null;

/**
 * Creates and starts the audio inside the click handler itself, since
 * Safari only allows audio to start during a user gesture.
 */
const toggleMusic = () => {
    isMusicOn.value = !isMusicOn.value;

    if (isMusicOn.value && isPlaying.value) {
        music ??= createAmbientMusic();
        music.play();
    }
};

watch(
    () => isMusicOn.value && isPlaying.value,
    (shouldPlay) => {
        if (shouldPlay) {
            music ??= createAmbientMusic();
            music.play();
        } else {
            music?.pause();
        }
    },
);

onUnmounted(() => music?.close());

const isFullscreenSupported =
    document.fullscreenEnabled || document.webkitFullscreenEnabled;

const currentFullscreenElement = () =>
    document.fullscreenElement ?? document.webkitFullscreenElement;

useEventListener(
    document,
    ["fullscreenchange", "webkitfullscreenchange"],
    () => {
        fullscreenElement.value = currentFullscreenElement() ?? null;
    },
);

/**
 * Calls the Fullscreen API synchronously inside the click, since Safari
 * only honors a request made during the gesture itself. A rejected request
 * is logged rather than thrown (production builds strip the log).
 */
const toggleFullscreen = async () => {
    if (!isFullscreenSupported) {
        isPseudoFullscreen.value = !isPseudoFullscreen.value;

        return;
    }

    try {
        if (currentFullscreenElement()) {
            await (
                document.exitFullscreen ?? document.webkitExitFullscreen
            ).call(document);
        } else {
            const element = container.value;

            await (
                element.requestFullscreen ?? element.webkitRequestFullscreen
            ).call(element);
        }
    } catch (error) {
        console.warn("Fullscreen request failed:", error);
    }
};

const fullscreenButton = useTemplateRef("fullscreenButton");
let isFullscreenToggledThisPress = false;

/**
 * Toggles on the earliest event of a press where Safari reports user
 * activation. Some browser extensions react to button clicks in a way that
 * uses up Safari's one-request-per-gesture fullscreen allowance before any
 * click listener runs, and Safari doesn't always grant activation as early
 * as pointerdown, so the request goes out on whichever of pointerdown,
 * mousedown or click first has it. Keyboard (Enter/Space) only fires click.
 * Listening in the capture phase on window runs ahead of everything else.
 */
useEventListener(
    window,
    ["pointerdown", "mousedown", "click"],
    (event) => {
        if (
            event.button !== 0 ||
            !fullscreenButton.value?.contains(event.target)
        ) {
            return;
        }

        if (event.type === "pointerdown") {
            isFullscreenToggledThisPress = false;
        }

        // Exiting needs no activation, so it never waits for a later event.
        // The pseudo fullscreen needs no activation at all and uses click.
        const canToggle = isFullscreenSupported
            ? Boolean(currentFullscreenElement()) ||
              (navigator.userActivation?.isActive ??
                  event.type !== "pointerdown")
            : event.type === "click";

        if (isFullscreenToggledThisPress || !canToggle) {
            if (event.type === "click") {
                isFullscreenToggledThisPress = false;
            }

            return;
        }

        isFullscreenToggledThisPress = event.type !== "click";
        toggleFullscreen();
    },
    { capture: true },
);

const isPageScrollLocked = useScrollLock(document.body);

watch(isPseudoFullscreen, (isOpen) => {
    isPageScrollLocked.value = isOpen;
});

onKeyStroke("Escape", () => {
    isPseudoFullscreen.value = false;
});

/** Overlay controls show on hover or keyboard focus, and always on touch screens. */
const controlGroupClass =
    "absolute bottom-3 z-10 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100 [@media(hover:none)]:opacity-100";
const controlButtonClass =
    "flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white/80 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent";
</script>

<template>
    <!-- Holds the animation's place in the page while it's teleported. -->
    <div class="size-full">
        <Teleport to="body" :disabled="!isPseudoFullscreen">
            <div
                :class="
                    isPseudoFullscreen
                        ? 'fixed inset-0 z-1000 flex items-center justify-center bg-black'
                        : 'size-full'
                "
            >
                <div
                    ref="container"
                    class="group relative overflow-hidden bg-midnight text-left"
                    :class="
                        isPseudoFullscreen
                            ? 'aspect-video w-[min(100vw,calc(100dvh*16/9))]'
                            : 'size-full'
                    "
                >
                    <div
                        role="img"
                        aria-label="Animated walkthrough of Qualta: create the EarBuds Product Feedback guide, choose the Product Feedback prompt template, publish the guide, copy its public link, share it in a social post, hold an AI interview with a respondent, open the Qualta Summary of the interviews, and ask Qualta how to improve the EarBuds."
                        class="absolute left-0 top-0 origin-top-left"
                        :class="{ 'process-animation-paused': !isPlaying }"
                        :style="{
                            width: `${STAGE_WIDTH}px`,
                            height: `${STAGE_HEIGHT}px`,
                            transform: `translate(${stageOffset.x}px, ${stageOffset.y}px) scale(${stageScale})`,
                        }"
                    >
                        <SceneTitle
                            v-if="prefersReducedMotion"
                            :time="Number.MAX_SAFE_INTEGER"
                        />
                        <Transition
                            v-else
                            enter-active-class="transition-opacity duration-500"
                            enter-from-class="opacity-0"
                            leave-active-class="transition-opacity duration-500"
                            leave-to-class="opacity-0"
                        >
                            <component
                                :is="scenes[sceneIndex].component"
                                :key="sceneIndex"
                                :time="sceneTime"
                                class="absolute inset-0"
                            />
                        </Transition>
                    </div>
                    <div
                        v-if="!prefersReducedMotion"
                        class="left-3"
                        :class="controlGroupClass"
                    >
                        <button
                            type="button"
                            ref="fullscreenButton"
                            :class="controlButtonClass"
                        >
                            <faIcon
                                :icon="
                                    isFullscreen
                                        ? 'fass fa-down-left-and-up-right-to-center'
                                        : 'fass fa-up-right-and-down-left-from-center'
                                "
                                class="size-3!"
                                aria-hidden="true"
                            />
                            <span class="sr-only">{{
                                isFullscreen
                                    ? "Exit full screen"
                                    : "View full screen"
                            }}</span>
                        </button>
                    </div>
                    <div
                        v-if="!prefersReducedMotion"
                        class="right-3"
                        :class="controlGroupClass"
                    >
                        <button
                            type="button"
                            :class="controlButtonClass"
                            :aria-pressed="isMusicOn"
                            @click="toggleMusic"
                        >
                            <faIcon
                                :icon="
                                    isMusicOn
                                        ? 'fass fa-volume'
                                        : 'fass fa-volume-xmark'
                                "
                                class="size-3.5!"
                                aria-hidden="true"
                            />
                            <span class="sr-only">Background music</span>
                        </button>
                        <button
                            type="button"
                            :class="controlButtonClass"
                            @click="isPausedByUser = !isPausedByUser"
                        >
                            <faIcon
                                :icon="
                                    isPausedByUser
                                        ? 'fass fa-play'
                                        : 'fass fa-pause'
                                "
                                class="size-3!"
                                aria-hidden="true"
                            />
                            <span class="sr-only">{{
                                isPausedByUser
                                    ? "Play animation"
                                    : "Pause animation"
                            }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style>
/* Shared by the scenes for elements that pop into view. */
.process-animation-enter {
    animation: process-animation-enter 300ms ease-out both;
}

@keyframes process-animation-enter {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Freeze CSS-driven motion (typing dots, squiggles, pings) with the clock. */
.process-animation-paused * {
    animation-play-state: paused !important;
}
</style>
